<template>
  <div class="section">
    <div class="container custom-container">
      <h1 class="title">
        <Loader v-bind:isLoading="isLoading" />&nbsp;Materials
      </h1>

      <!-- Table showing the user's materials -->
      <div class="my-materials-table">
        <table class="table is-striped is-fullwidth">
          <thead>
            <tr>
              <th class="my-vertical-align">Name</th>
              <th class="my-vertical-align">
                Density
                <br />
                <span class="is-size-7 has-text-weight-normal">
                  kg/m
                  <sup>3</sup>
                </span>
              </th>
              <th class="my-vertical-align">
                Conductivity
                <br />
                <span class="is-size-7 has-text-weight-normal">S/m</span>
              </th>
              <th class="my-vertical-align">
                Rel. Permittivity
                <br />
                <span class="is-size-7 has-text-weight-normal has-text-white">1</span>
              </th>
              <th class="my-vertical-align">
                Thermal Conductivity
                <br />
                <span class="is-size-7 has-text-weight-normal">W/(m&middot;&deg;C)</span>
              </th>
              <th class="my-vertical-align">
                Heat Capacity
                <br />
                <span class="is-size-7 has-text-weight-normal">J/(kg&middot;&deg;C)</span>
              </th>
              <th class="my-vertical-align">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="mat in materials"
              :key="mat.id"
              v-bind:class="{ 'has-text-grey-light': mat.isLoading}"
            >
              <td class="my-vertical-align">{{mat.name}}</td>
              <td class="my-vertical-align">{{mat.density}}</td>
              <td class="my-vertical-align">{{mat.conductivity}}</td>
              <td class="my-vertical-align">{{mat.permittivity}}</td>
              <td class="my-vertical-align">{{mat.thermal_conductivity}}</td>
              <td class="my-vertical-align">{{mat.heat_capacity}}</td>
              <td>
                <div v-if="('isLoading' in mat) && mat.isLoading">
                  <Loader isLoading />
                </div>
                <div v-else>
                  <p class="buttons" v-if="mat.status==1">
                    <button
                      class="button is-primary"
                      v-on:click="loadMaterial"
                      v-bind:data-material-id="mat.id"
                    >
                      <span class="icon">
                        <font-awesome-icon icon="pencil-alt" />
                      </span>
                    </button>
                    <button
                      class="button is-danger"
                      v-on:click="toggleModalDelete"
                      v-bind:data-material-id="mat.id"
                    >
                      <span class="icon">
                        <font-awesome-icon icon="trash-alt" />
                      </span>
                    </button>
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Modal: Confirmation dialog before deleting a material -->
      <div class="modal" v-bind:class="{'is-active': activeModalDelete}">
        <div class="modal-background" v-on:click="toggleModalDelete"></div>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Please Confirm</p>
            <button class="delete" aria-label="close" v-on:click="toggleModalDelete"></button>
          </header>
          <section class="modal-card-body">
            <p>Are you sure you want to delete this material?</p>
          </section>
          <footer class="modal-card-foot">
            <button class="button is-danger" v-on:click="deleteMaterial">Delete</button>
            <button class="button" v-on:click="toggleModalDelete">Cancel</button>
          </footer>
        </div>
        <button class="modal-close is-large" aria-label="close" v-on:click="toggleModalDelete"></button>
      </div>

      <!-- Modal: Create or edit material -->
      <div class="modal" v-bind:class="{'is-active': activeModalEdit}">
        <div class="modal-background" v-on:click="toggleModalEdit"></div>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Material Properties</p>
            <button class="delete" aria-label="close" v-on:click="toggleModalEdit"></button>
          </header>
          <section class="modal-card-body">
            <div class="columns">
              <div class="column is-one-third">
                <!-- Name -->
                <div class="field">
                  <label class="label">Name</label>
                  <p class="control">
                    <input class="input" type="text" placeholder="Text input" v-model="name" />
                  </p>
                </div>
                <Field name="Density" v-model="density" units="kg/m<sup>3</sup>" :vpositive="true" />
                <Field name="Conductivity" v-model="conductivity" units="S/m" :vpositive="true" />
                <Field name="Relative Permittivity" v-model="permittivity" units="" :vpositive="true" />
                <Field
                  name="Thermal Conductivity"
                  v-model="thermal_conductivity"
                  units="W/(m&middot;&deg;C)"
                  :vpositive="true"
                />
                <Field
                  name="Heat Capacity"
                  v-model="heat_capacity"
                  units="J/(kg&middot;&deg;C)"
                  :vpositive="true"
                />
              </div>
            </div>
          </section>
          <footer class="modal-card-foot">
            <button class="button is-primary" v-on:click="save">Save</button>
            <button class="button" v-on:click="toggleModalEdit">Cancel</button>
          </footer>
        </div>
        <button class="modal-close is-large" aria-label="close" v-on:click="toggleModalEdit"></button>
      </div>

      <h2 class="title is-4">New Material</h2>
      <button class="button is-primary" v-on:click="toggleModalEdit">Create</button>
    </div>
  </div>
</template>
<script>
import Field from '@/components/Simulation/Field.vue';
import Loader from '@/components/Loader.vue';
import apiCall from '../lib/remote-calls';

export default {
  components: {
    Field,
    Loader,
  },
  data() {
    return {
      // values for one material being created or edited
      name: '',
      density: '',
      conductivity: '',
      permittivity: '',
      thermal_conductivity: '',
      heat_capacity: '',

      // for the modals
      activeMaterial: null,
      activeModalDelete: false,
      activeModalEdit: false,

      // this will come from the database
      materials: [],
      isLoading: false,
    };
  },
  methods: {
    async createMaterial() {
      const material = {
        id: null, // will come from database
        isLoading: true,
        status: 1,
        name: this.name,
        density: this.density,
        conductivity: this.conductivity,
        permittivity: this.permittivity,
        thermal_conductivity: this.thermal_conductivity,
        heat_capacity: this.heat_capacity,
      };
      this.toggleModalEdit();
      this.materials.push(material);
      try {
        const response = await apiCall('POST', 'materials', { data: material });
        material.id = parseInt(response.data.material_id, 10);
        material.isLoading = false;
      } catch (error) {
        console.error(error);
      }
    },
    async requestMaterials() {
      this.isLoading = true;
      console.log('requesting materials');
      try {
        const response = await apiCall('GET', 'materials');
        console.log('got response', response);
        this.materials = response.data.materials;
      } catch (error) {
        console.error(error);
      }
      this.isLoading = false;
    },
    async updateMaterial() {
      const databaseId = this.activeMaterial;
      const arrayIndex = this.materials.findIndex((mat) => mat.id === databaseId);
      this.materials[arrayIndex].isLoading = true;

      const material = {
        id: databaseId,
        name: this.name,
        isLoading: false,
        status: 1,
        density: this.density,
        conductivity: this.conductivity,
        permittivity: this.permittivity,
        thermal_conductivity: this.thermal_conductivity,
        heat_capacity: this.heat_capacity,
      };

      this.toggleModalEdit();

      try {
        await apiCall('PUT', `materials/${databaseId}`, { data: material });
        this.$set(this.materials, arrayIndex, material);
      } catch (error) {
        console.error(error);
      }
    },
    async deleteMaterial() {
      const databaseId = this.activeMaterial;
      this.toggleModalDelete(null); // will also reset this.activeMaterial

      let arrayIndex = null;
      const remainingMaterials = this.materials.filter((el, index) => {
        if (el.id === databaseId) {
          arrayIndex = index;
          return false;
        }
        return true;
      });

      try {
        this.materials[arrayIndex].isLoading = true; // for presentation purposes
        await apiCall('DELETE', `materials/${databaseId}`);
      } catch (error) {
        this.materials[arrayIndex].isLoading = false;
        console.error(error);
      }
      this.materials = remainingMaterials;
    },
    save() {
      if (this.activeMaterial == null) {
        this.createMaterial();
      } else {
        this.updateMaterial();
      }
    },
    toggleModalDelete(event) {
      if (this.activeModalDelete) {
        this.activeMaterial = null;
      } else {
        this.activeMaterial = parseInt(
          event.currentTarget.dataset.materialId,
          10,
        );
      }
      this.activeModalDelete = !this.activeModalDelete;
    },
    loadMaterial(event) {
      this.activeMaterial = parseInt(
        event.currentTarget.dataset.materialId,
        10,
      );
      const material = this.materials.find(
        (mat) => mat.id === this.activeMaterial,
      );
      this.name = material.name;
      this.density = material.density;
      this.conductivity = material.conductivity;
      this.permittivity = material.permittivity;
      this.thermal_conductivity = material.thermal_conductivity;
      this.heat_capacity = material.heat_capacity;
      this.activeModalEdit = true;
    },
    toggleModalEdit() {
      if (this.activeModalEdit) {
        this.name = '';
        this.density = '';
        this.conductivity = '';
        this.permittivity = '';
        this.thermal_conductivity = '';
        this.heat_capacity = '';
        this.activeMaterial = null;
      }
      this.activeModalEdit = !this.activeModalEdit;
    },
  },
  mounted() {
    this.requestMaterials();
  },
};
</script>
<style>
div.my-materials-table {
  margin-top: 2.5em;
  margin-bottom: 2em;
}

td.my-vertical-align {
  vertical-align: middle !important;
}
</style>
